import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Alert } from '@/components/ui'

import type { GraphPanelShellProps } from '../types'

/**
 * The card the acknowledgement page's telemetry graph sits in: heading, the four
 * load/error/empty/ready states, and the height measurement the charts need (they
 * take an explicit pixel height rather than filling their container).
 *
 * `children` is only called once a height is known, so a chart never mounts at 0px.
 */
const GraphPanelShell: React.FC<GraphPanelShellProps> = ({
  title,
  className = '',
  error,
  isLoading,
  hasData,
  emptyMessage,
  children,
}) => {
  const [graphHeight, setGraphHeight] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const setGraphContainer = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? 0
      // Total budget for the chart: the charts measure their own nav bar and
      // subtract it, so no allowance for the nav is needed here.
      setGraphHeight(Math.max(0, Math.floor(height)))
    })
    observer.observe(el)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return (
    <div className={`rounded-md bg-white pt-2 pl-4 pr-4 pb-2 shadow-sm flex flex-col min-h-0 ${className}`}>
      <p className="text-sm font-medium text-primary mb-1">{title}</p>
      <div ref={setGraphContainer} className="flex-1 min-h-0 p-4">
        {error ? (
          <Alert type="error" message={error} />
        ) : isLoading && !hasData ? (
          <div className="h-full animate-pulse rounded bg-base-200" />
        ) : !hasData ? (
          // Must come before the chart: the charts take a non-optional `data` prop and
          // would sit on a permanent spinner if handed nothing.
          <Alert type="info" message={emptyMessage} />
        ) : graphHeight > 0 ? (
          children(graphHeight)
        ) : null}
      </div>
    </div>
  )
}

export default GraphPanelShell
